import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../../../apiConfig';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link
} from '@mui/material';
import { PhotoCamera, UploadFile, Delete as DeleteIcon, Edit as EditIcon, Clear as ClearIcon } from '@mui/icons-material';
import CustomTypography from '../../UI/Typography/CustomTypography';
// Assuming you have a CustomAlert component or similar for notifications
// import CustomAlert from '../../UI/Alert/CustomAlert';

// Import ReactQuill and its styles
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { styled } from '@mui/material/styles';

// Styling for Items in Grid, similar to BoardAdminNews
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#F9F9F9',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

const AdminAwarenessMaterial = () => {
  const [title, setTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [fullArticleContent, setFullArticleContent] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [copyToNews, setCopyToNews] = useState(false);
  const [existingMaterials, setExistingMaterials] = useState([]);
  const [pdfFileName, setPdfFileName] = useState('');
  const [iconFileName, setIconFileName] = useState('');
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);
  const [existingPdfUrl, setExistingPdfUrl] = useState(null);
  const [existingIconUrl, setExistingIconUrl] = useState(null);
  const [removePdfFileOnUpdate, setRemovePdfFileOnUpdate] = useState(false);
  const [removeIconFileOnUpdate, setRemoveIconFileOnUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'info' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [materialToDeleteId, setMaterialToDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const formRef = useRef(null);

  const fetchAwarenessMaterials = useCallback(async () => {
    setIsFetching(true);
    try {
      const userString = localStorage.getItem('user');
      const jwtToken = userString ? JSON.parse(userString).token : null;
      const headers = jwtToken ? { 'x-access-token': jwtToken } : {};
      const response = await axios.get(`${API_URL}/awareness-material`, { headers });
      setExistingMaterials(response.data || []);
    } catch (error) {
      console.error('Error fetching awareness materials:', error.response ? error.response.data : error.message);
      setAlertInfo({ open: true, message: 'Failed to fetch awareness materials.', severity: 'error' });
      setExistingMaterials([]);
    }
    setIsFetching(false);
  }, []);

  useEffect(() => {
    fetchAwarenessMaterials();
  }, [fetchAwarenessMaterials]);

  const handlePdfFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPdfFile(file);
      setPdfFileName(file.name);
      setRemovePdfFileOnUpdate(false);
    } else {
      setPdfFile(null);
      setPdfFileName('');
    }
  };

  const handleIconFileChange = (event) => {
    const file = event.target.files[0];
    if (iconPreviewUrl) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    if (file) {
      setIconFile(file);
      setIconFileName(file.name);
      setIconPreviewUrl(URL.createObjectURL(file));
      setRemoveIconFileOnUpdate(false);
    } else {
      setIconFile(null);
      setIconFileName('');
      setIconPreviewUrl(null);
    }
  };

  const resetForm = () => {
    setTitle('');
    setShortDescription('');
    setFullArticleContent('');
    setPdfFile(null);
    setIconFile(null);
    setPdfFileName('');
    setIconFileName('');
    if (iconPreviewUrl) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    setIconPreviewUrl(null);
    setCopyToNews(false);
    setEditingId(null);
    setExistingPdfUrl(null);
    setExistingIconUrl(null);
    setRemovePdfFileOnUpdate(false);
    setRemoveIconFileOnUpdate(false);
    if (formRef.current) {
      const fileInputs = formRef.current.querySelectorAll('input[type="file"]');
      fileInputs.forEach(input => input.value = null);
    }
  };

  const handleEdit = (material) => {
    setEditingId(material.id);
    setTitle(material.title || '');
    setShortDescription(material.shortDescription || '');
    setFullArticleContent(material.fullArticleContent || '');
    setCopyToNews(false);

    setPdfFile(null);
    setPdfFileName('');
    setExistingPdfUrl(material.pdfFileUrl || null);
    setRemovePdfFileOnUpdate(false);

    setIconFile(null);
    setIconFileName('');
    if (iconPreviewUrl) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    setIconPreviewUrl(null);
    setExistingIconUrl(material.iconFileUrl || null);
    setRemoveIconFileOnUpdate(false);
    
    setAlertInfo({open: false, message: '', severity: 'info'});
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append('title', title);
    formData.append('shortDescription', shortDescription);
    formData.append('fullArticleContent', fullArticleContent);
    
    if (pdfFile) {
      formData.append('pdfFile', pdfFile);
    } else if (editingId && removePdfFileOnUpdate) {
      formData.append('removePdfFile', 'true');
    }

    if (iconFile) {
      formData.append('iconFile', iconFile);
    } else if (editingId && removeIconFileOnUpdate) {
      formData.append('removeIconFile', 'true');
    }
    
    if (!editingId) {
        formData.append('copyToNews', copyToNews);
    }

    const userString = localStorage.getItem('user');
    const jwtToken = userString ? JSON.parse(userString).token : null;

    if (!jwtToken) {
      setAlertInfo({ open: true, message: 'Authentication token not found. Please log in.', severity: 'error' });
      setIsLoading(false);
      return;
    }

    formData.append('jwtToken', jwtToken);

    try {
      let response;
      const requestHeaders = {
        'Content-Type': 'multipart/form-data',
      };

      if (editingId) {
        response = await axios.put(`${API_URL}/awareness-material/${editingId}`, formData, {
          headers: requestHeaders,
        });
        setAlertInfo({ open: true, message: 'Awareness material updated successfully!', severity: 'success' });
      } else {
        response = await axios.post(`${API_URL}/awareness-material`, formData, {
          headers: requestHeaders,
        });
        setAlertInfo({ open: true, message: 'Awareness material created successfully!', severity: 'success' });
      }
      console.log('Operation successful:', response.data);
      resetForm();
      fetchAwarenessMaterials();
    } catch (error) {
      console.error('Error saving awareness material:', error.response ? error.response.data : error.message);
      const errorMessage = error.response && error.response.data && error.response.data.message 
                           ? error.response.data.message 
                           : `Failed to ${editingId ? 'update' : 'create'} awareness material.`;
      setAlertInfo({ open: true, message: errorMessage, severity: 'error' });
    }
    setIsLoading(false);
  };
  
  const openDeleteDialog = (id) => {
    setMaterialToDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setMaterialToDeleteId(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!materialToDeleteId) return;
    setIsLoading(true);
    closeDeleteDialog();
    const userString = localStorage.getItem('user');
    const jwtToken = userString ? JSON.parse(userString).token : null;

    if (!jwtToken) {
      setAlertInfo({ open: true, message: 'Authentication token not found. Please log in.', severity: 'error' });
      setIsLoading(false);
      return;
    }
    try {
      await axios.delete(`${API_URL}/awareness-material/${materialToDeleteId}`, {
        headers: { 'x-access-token': jwtToken },
      });
      setAlertInfo({ open: true, message: 'Awareness material deleted successfully!', severity: 'success' });
      fetchAwarenessMaterials();
    } catch (error) {
      console.error('Error deleting awareness material:', error.response ? error.response.data : error.message);
      const errorMessage = error.response && error.response.data && error.response.data.message 
                           ? error.response.data.message 
                           : 'Failed to delete awareness material.';
      setAlertInfo({ open: true, message: errorMessage, severity: 'error' });
    }
    setIsLoading(false);
  };

  const SimpleAlert = ({ open, message, severity, onClose }) => {
    if (!open) return null;
    return (
      <Box sx={{ p: 2, mb: 2, border: 1, borderColor: `${severity}.main`, color: `${severity}.contrastText`, backgroundColor: `${severity}.light`, borderRadius: 1 }}>
        {message}
        <Button onClick={onClose} size="small" sx={{ml: 2}}>Close</Button>
      </Box>
    );
  };

  // Effect for cleaning up object URL on unmount
  useEffect(() => {
    return () => {
      if (iconPreviewUrl) {
        URL.revokeObjectURL(iconPreviewUrl);
      }
    };
  }, [iconPreviewUrl]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Banner similar to BoardAdminNews */}
      <Grid container spacing={2} sx={{ mb: 3, mt: 2 }}> {/* Added some margin */}
        <Grid item xs={12}>
          <Item sx={{ 
            paddingTop: '30px', 
            paddingBottom: '30px', 
            backgroundImage: 'url("/images/BannerImages/2.jpg")', // Using the same banner as BoardAdminEvents
            backgroundSize: 'cover', 
            backgroundPosition: 'center' 
          }}>
            <CustomTypography variant="h3" text="Manage Awareness Material" color="white" />
          </Item>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, ref:formRef }}>
        <CustomTypography variant="h4" text={editingId ? "Edit Awareness Material" : "Manage Awareness Material"} gutterBottom />
        
        <SimpleAlert 
            open={alertInfo.open} 
            message={alertInfo.message} 
            severity={alertInfo.severity} 
            onClose={() => setAlertInfo({...alertInfo, open: false})} 
        />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Short Description"
                fullWidth
                multiline
                rows={3}
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Full Article Content
              </Typography>
              <ReactQuill 
                theme="snow"
                value={fullArticleContent}
                onChange={setFullArticleContent}
                placeholder="Enter full article content here..."
                style={{ backgroundColor: isLoading ? '#f0f0f0' : 'white' }} // Optional: visual cue for disabled state
                readOnly={isLoading} // Make Quill read-only when loading
                modules={{
                  toolbar: [
                    [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
                    [{size: []}],
                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                    [{'list': 'ordered'}, {'list': 'bullet'}, 
                     {'indent': '-1'}, {'indent': '+1'}],
                    ['link', 'image', 'video'], // 'image' and 'video' might need further setup depending on backend/storage
                    ['clean']
                  ],
                }}
                formats={[
                  'header', 'font', 'size',
                  'bold', 'italic', 'underline', 'strike', 'blockquote',
                  'list', 'bullet', 'indent',
                  'link', 'image', 'video'
                ]}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth startIcon={<UploadFile />} disabled={isLoading}>
                {editingId && existingPdfUrl ? 'Replace PDF' : 'Upload PDF (.pdf)'}
                <input type="file" hidden accept=".pdf" onChange={handlePdfFileChange} />
              </Button>
              {pdfFileName && <Typography variant="caption" display="block" sx={{mt:1}}>New: {pdfFileName}</Typography>}
              {editingId && existingPdfUrl && !pdfFile && (
                <Box sx={{display: 'flex', alignItems: 'center', mt:1}}>
                  <Typography variant="caption" sx={{mr:1}}>Current: <Link href={existingPdfUrl} target="_blank" rel="noopener noreferrer">{existingPdfUrl.split('/').pop()}</Link></Typography>
                  {!removePdfFileOnUpdate && 
                    <IconButton size="small" onClick={() => setRemovePdfFileOnUpdate(true)} title="Mark for removal">
                      <ClearIcon fontSize="small" color="error"/>
                    </IconButton>
                  }
                  {removePdfFileOnUpdate && 
                    <Typography variant="caption" color="error" sx={{ml:1}}>(Marked for removal)</Typography>
                  }
                </Box>
              )}
              {editingId && existingPdfUrl && removePdfFileOnUpdate &&
                <Button size="small" onClick={() => setRemovePdfFileOnUpdate(false)} sx={{mt:0.5}}>Undo remove PDF</Button>
              }
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth startIcon={<PhotoCamera />} disabled={isLoading}>
                {editingId && existingIconUrl ? 'Replace Icon' : 'Upload Icon (Image)'}
                <input type="file" hidden accept="image/*" onChange={handleIconFileChange} />
              </Button>
              {/* Display name of newly selected file */}
              {iconFileName && <Typography variant="caption" display="block" sx={{mt:1}}>New: {iconFileName}</Typography>}
              
              {/* Display new icon preview */}
              {iconPreviewUrl && (
                <Box sx={{mt: 1, border: '1px dashed grey', width: 'fit-content', p: 0.5}}>
                  <img src={iconPreviewUrl} alt="New icon preview" style={{ maxHeight: '100px', maxWidth: '200px', display:'block' }} />
                </Box>
              )}

              {/* Display existing icon details when editing AND no new icon is staged for upload */}
              {editingId && existingIconUrl && !iconFile && ( // iconFile check ensures we don't show this if a new file is chosen
                <Box sx={{mt:1}}>
                  <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <Typography variant="caption" sx={{mr:1}}>Current:</Typography>
                    <img src={existingIconUrl} alt="Current icon" style={{maxHeight: '20px', verticalAlign: 'middle', marginRight: '5px'}} /> 
                    <Link href={existingIconUrl} target="_blank" rel="noopener noreferrer">{existingIconUrl.split('/').pop()}</Link>
                    {!removeIconFileOnUpdate && (
                      <IconButton size="small" onClick={() => setRemoveIconFileOnUpdate(true)} title="Mark for removal" sx={{ml: 0.5}}>
                        <ClearIcon fontSize="small" color="error"/>
                      </IconButton>
                    )}
                  </Box>
                  {removeIconFileOnUpdate && (
                    <Box sx={{display: 'flex', alignItems: 'center', mt: 0.5}}>
                       <Typography variant="caption" color="error">(Marked for removal)</Typography>
                       <Button size="small" onClick={() => setRemoveIconFileOnUpdate(false)} sx={{ml:1, textTransform: 'none'}}>Undo</Button>
                    </Box>
                  )}
                </Box>
              )}
            </Grid>

            {!editingId && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={copyToNews} onChange={(e) => setCopyToNews(e.target.checked)} disabled={isLoading} />}
                  label="Copy to Latest News"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" disabled={isLoading || isFetching}>
                {isLoading ? <CircularProgress size={24} /> : (editingId ? 'Update Awareness Material' : 'Add Awareness Material')}
              </Button>
              {editingId && (
                <Button variant="outlined" color="secondary" onClick={handleCancelEdit} sx={{ ml: 2 }} disabled={isLoading}>
                  Cancel Edit
                </Button>
              )}
            </Grid>
          </Grid>
        </form>

        <Box sx={{ mt: 5 }}>
          <CustomTypography variant="h5" text="Existing Awareness Materials" gutterBottom />
          {isFetching ? <CircularProgress /> : (
            existingMaterials.length === 0 ? (
              <Typography>No awareness materials uploaded yet.</Typography>
            ) : (
              <List>
                {existingMaterials.map((material) => (
                  <ListItem 
                    key={material.id} 
                    divider
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                      <ListItemText 
                        primary={material.title} 
                        secondary={`Author: ${material.author || 'N/A'} - Updated: ${new Date(material.updatedAt).toLocaleDateString()}`}
                        sx={{ mr: 2, flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      />
                      {material.iconFileUrl && 
                        <img 
                          src={material.iconFileUrl} 
                          alt="icon" 
                          style={{maxHeight: '30px', marginRight: '10px', flexShrink: 0}}
                        />}
                      {material.pdfFileUrl && 
                        <Link 
                          href={material.pdfFileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ mr: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                          View PDF
                        </Link>}
                    </Box>
                    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                        <IconButton edge="end" aria-label="edit" onClick={() => handleEdit(material)} sx={{mr: 0.5}} disabled={isLoading || editingId === material.id}>
                          <EditIcon />
                        </IconButton>
                        <IconButton edge="end" aria-label="delete" onClick={() => openDeleteDialog(material.id)} disabled={isLoading}>
                          <DeleteIcon />
                        </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )
          )}
        </Box>
      </Paper>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this awareness material? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={isLoading}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default AdminAwarenessMaterial; 